import React, {useEffect, useMemo} from "react"
import classNames from "classnames"
import {Assignee} from "../../resources/application"
import {
	AllowedProductApplicationsColumns,
	DecisionMethodTypeEnum,
	findProductApplication,
	ProductApplicationStatus,
	ProductType,
} from "../../resources/productApplication"
import {
	useAccessToken,
	useIsBankUser,
	useIsUnitComplianceUser,
	useIsUnitSuperUser,
	useUserType,
} from "../../services/auth"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {usePaging} from "../../hooks/usePaging"
import {useQueryState} from "use-location-state"
import SearchBox from "../../components/SearchBox/SearchBox"
import {Filter} from "../Filter/Filter"
import {ErrorDocument} from "../../resources/common"
import {startCase} from "lodash"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {useIsAllowedToShowAssigneeFilter} from "../../services/permission"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import {findOrgs} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {Org} from "../../resources/org"
import ProductApplicationsTable from "./ProductApplicationsTable"

interface ProductApplicationsProps {
	token?: string
	limit?: number
	includedColumns: Array<AllowedProductApplicationsColumns>
	enableTitle?: boolean
	fullHeight?: boolean
	customerId?: string
	refreshToken: number
	showTabsOnHeader?: boolean
}

export function ProductApplications({
	limit = 25,
	includedColumns,
	fullHeight,
	enableTitle,
	customerId,
	refreshToken,
	showTabsOnHeader,
}: ProductApplicationsProps) {
	const accessToken = useAccessToken()
	const userType = useUserType()
	const prefix = "product-app"

	const statusesOptions = new Map(Object.values(ProductApplicationStatus).map((v) => [v, startCase(v)]))
	const [statuses, setStatuses] = useQueryState(`${prefix}-filter[status]`, Array.from(statusesOptions.keys()))

	const decisionMethodOptions = new Map(Object.values(DecisionMethodTypeEnum).map((v) => [v, startCase(v)]))
	const [decisionMethods, setDecisionMethods] = useQueryState(
		`${prefix}-filter[decisionMethod]`,
		Array.from(decisionMethodOptions.keys())
	)

	const productOptions = new Map(Object.values(ProductType).map((v) => [v, v]))
	const [requestedProducts, setRequestedProducts] = useQueryState<ProductType[]>(
		`${prefix}-filter[requestedProduct]`,
		Array.from(productOptions.keys())
	)

	const assigneeOptions = new Map(Object.values(Assignee).map((v) => [v, startCase(v)]))
	const [assignees, setAssignees] = useQueryState<Assignee[]>(
		`${prefix}-filter[assignee]`,
		Array.from(assigneeOptions.keys())
	)

	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")

	const [query, setQuery] = useQueryState(`${prefix}-filter[query]`, "")

	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>(`${prefix}-filter[orgs]`, [])

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) =>
			findProductApplication(
				accessToken,
				offset,
				limit,
				statuses,
				sortBy,
				query,
				filteredOrgs,
				requestedProducts,
				decisionMethods,
				assignees,
				customerId
			),
		(x) => x.data.length,
		[
			sortBy,
			query,
			[...statuses, ...filteredOrgs, ...requestedProducts, ...decisionMethods, ...assignees].join(","),
			customerId,
			refreshToken,
		],
		prefix,
		true
	)
	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Product Applications", 500, onSearch)

	const orgs =
		userType === "unit" || userType === "bank"
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()

	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const assigneeFilter = (
		<Filter
			title="Assignee"
			statuses={assignees}
			setStatuses={setAssignees}
			onFilterFunc={() => reset(limit)}
			options={assigneeOptions}
		/>
	)

	const statusFilter = (
		<Filter
			title="Status"
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
		/>
	)

	const decisionMethodFilter = (
		<Filter
			title="Decision Method"
			statuses={decisionMethods}
			setStatuses={setDecisionMethods}
			onFilterFunc={() => reset(limit)}
			options={decisionMethodOptions}
		/>
	)

	const productsFilter = (
		<Filter
			title="Products"
			statuses={requestedProducts}
			setStatuses={setRequestedProducts}
			onFilterFunc={() => reset(limit)}
			options={productOptions}
		/>
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function toggleSort() {
		setSortBy(sortBy == "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	return (
		<DataTableCard className={"applications-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={searchBox}
				filters={[
					...(customerId ? [] : [orgFilter]),
					productsFilter,
					statusFilter,
					...(useIsUnitSuperUser() || useIsUnitComplianceUser() || useIsBankUser() ? [decisionMethodFilter] : []),
					...(useIsAllowedToShowAssigneeFilter() ? [assigneeFilter] : []),
				]}
				title={enableTitle ? "Product Applications" : null}
				className={classNames(showTabsOnHeader && "with-title-bar-tabs")}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: productApplications}) => {
					return (
						<ProductApplicationsTable
							productApplications={productApplications.data}
							include={productApplications.included}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={true}
							includedColumns={includedColumns}
							fullHeight={fullHeight}
							showTabsOnHeader={showTabsOnHeader}
							meta={productApplications.meta}
							sortFunction={toggleSort}
							sortBy={sortBy}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
